import { Link, navigate, routes } from '@cedarjs/router'
import { Typography } from '@mui/material'
import { toast } from 'react-hot-toast'
import React, { useEffect, useMemo } from 'react'
import SingleComplianceForm from 'src/components/SingleComplianceDrawer/SingleComplianceForm'
import useQuery from 'src/hooks/useQuery'
import { inProgressValues } from 'src/utils/maintenanceItem'
import IconButton from 'src/components/MUI/IconButton'
import { useOrgName } from 'src/hooks/useOrgName'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined'
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined'
import useHasPermission from 'src/hooks/useHasPermission'
import { Permissions } from '@wingwork/common/src/constants/permissions'
import Loading from 'src/components/Loading'
import Button from 'src/components/MUI/Button'

const GET_MAINTENANCE_ITEM = gql`
  query GetMaintenanceItemForSidebarCompliance($id: String!) {
    maintenanceItem(id: $id) {
      id
      workOrderIdInProgress
      complianceLedger {
        id
        status
        isRevision
        MaintenanceLogbook {
          id
          status
        }
      }
      aircraftId
    }
  }
`

interface ComplianceTabProps {
  id: string
  isOpen: boolean
  onClose: () => void
  setInProgress: (value: boolean) => void
}

const ComplianceTab: React.FC<ComplianceTabProps> = ({
  id,
  isOpen,
  onClose,
  setInProgress,
}) => {
  const orgSlug = useOrgName()
  const { data, hasLoaded, loading } = useQuery(GET_MAINTENANCE_ITEM, {
    variables: { id: id },
    skip: !id || !isOpen,
  })

  const maintenanceItem = data?.maintenanceItem ?? {}

  const {
    isLedgerInProgress,
    isRevisionInProgress,
    isLogbookInProgress,
    isLogbookAwaitingSignature,
    isWorkOrderInProgress,
    isMaintenanceItemInProgress,
  } = loading
    ? {
        isLedgerInProgress: false,
        isRevisionInProgress: false,
        isLogbookInProgress: false,
        isLogbookAwaitingSignature: false,
        isWorkOrderInProgress: false,
        isMaintenanceItemInProgress: false,
      }
    : inProgressValues(maintenanceItem)

  useEffect(() => {
    setInProgress(isMaintenanceItemInProgress)
  }, [isMaintenanceItemInProgress])

  const canReadCompliance = useMemo(() => {
    if (!data?.maintenanceItem?.aircraftId) return false
    return useHasPermission(
      Permissions.compliance.read,
      data?.maintenanceItem?.aircraftId
    )
  }, [data?.maintenanceItem?.aircraftId])

  if (!id) {
    return null
  }

  if (loading) {
    return <Loading />
  }

  if (isMaintenanceItemInProgress) {
    return (
      <div className="flex w-full items-center justify-center p-5">
        {isLedgerInProgress && (
          <div className="flex w-full items-center">
            <div className="flex flex-grow items-center gap-2">
              <FolderOutlinedIcon />
              <Typography variant="body2" className="flex-shrink-0">
                This task is currently in progress on an existing compliance
              </Typography>
            </div>
            <Button
              variant="text"
              color="black"
              className="flex-shrink-0"
              component={Link}
              to={routes.bulkCompliance({
                orgName: orgSlug,
                ledgerId: maintenanceItem.complianceLedger?.id,
              })}
            >
              View Compliance
            </Button>
          </div>
        )}
        {isRevisionInProgress && (
          <div className="flex w-full items-center">
            <div className="flex flex-grow items-center gap-2">
              <DifferenceOutlinedIcon />
              <Typography variant="body2" className="flex-shrink-0">
                This task is currently in progress on a compliance revision
              </Typography>
            </div>
            <Button
              variant="text"
              color="black"
              className="flex-shrink-0"
              component={Link}
              to={routes.bulkCompliance({
                orgName: orgSlug,
                ledgerId: maintenanceItem.complianceLedger?.id,
              })}
            >
              View Compliance
            </Button>
          </div>
        )}
        {isLogbookInProgress && (
          <>
            <Typography variant="body2">
              This task is waiting for the logbook to be published
            </Typography>
          </>
        )}
        {isLogbookAwaitingSignature && (
          <>
            <Typography variant="body2">
              This task is currently waiting for logbook signatures
            </Typography>
          </>
        )}
        {isWorkOrderInProgress && (
          <div className="flex w-full items-center">
            <div className="flex flex-grow items-center gap-2">
              <WorkOutlineOutlinedIcon />
              <Typography variant="body2" className="flex-shrink-0">
                This task is currently in progress on an existing work order
              </Typography>
            </div>
            <Button
              variant="text"
              color="black"
              className="flex-shrink-0"
              component={Link}
              to={routes.workOrder({
                orgName: orgSlug,
                id: maintenanceItem.workOrderIdInProgress,
              })}
            >
              View Compliance
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <SingleComplianceForm
      id={id}
      isOpen={isOpen}
      onCompleted={() => {
        toast.success('Compliance completed')
        onClose()
      }}
    />
  )
}

export default ComplianceTab
